import React, { useEffect, useState, useRef } from 'react';
import { getLogs, getLogDates } from '../../service/api/Logs/apiLogs';
import { Message } from '../../components';
import { useUser } from '../../hooks/useUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileAlt, faDownload, faFile, faSyncAlt, faInbox,
    faExclamationTriangle, faChevronLeft, faChevronRight,
    faAngleDoubleLeft, faAngleDoubleRight, faSearch,
    faSortAmountDown, faSortAmountUp
} from '@fortawesome/free-solid-svg-icons';
import '../../css/LogViewer.css';
import { getDeliver } from '../../service/api/Deliver/apiDeliver';
import { roleConfig } from '../../constants/roleConfig';

const LogViewer = () => {
    const [availableDates, setAvailableDates] = useState([]);
    const [typeAvailability, setTypeAvailability] = useState({});
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedType, setSelectedType] = useState('all');
    const [logContent, setLogContent] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDates, setLoadingDates] = useState(true);
    const [fileName, setFileName] = useState('');
    const [delivers, setDelivers] = useState(null);

    // 分頁相關
    const [pageSize, setPageSize] = useState(100);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        totalLines: 0,
        filteredCount: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
    });

    // 過濾相關
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [keyword, setKeyword] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [deliverId, setDeliverId] = useState('');

    // 排序
    const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

    const { role } = useUser();
    const isMounted = useRef(true);
    const isInitialized = useRef(false);

    const logTypes = [
        { value: 'all', label: '全部日誌', color: '#6c757d' },
        { value: 'error', label: '錯誤日誌', color: '#dc3545' },
        { value: 'info', label: '資訊日誌', color: '#0dcaf0' },
        { value: 'warning', label: '警告日誌', color: '#ffc107' },
    ];

    // 載入日誌內容
    const fetchLogs = async (date, type, page, size, sTime, eTime, sKeyword, order, userID) => {
        if (!date) return;

        setLoading(true);

        try {
            const response = await getLogs(date, type, page, size, sTime, eTime, sKeyword, order, userID);
            if (!isMounted.current) return;

            if (response?.data?.status === 1) {
                setLogContent(response.data.data.content);
                setFileName(response.data.data.fileName);
                setCurrentPage(response.data.data.pagination?.currentPage || 1);
                setPagination({
                    totalLines: response.data.data.pagination?.totalLines || 0,
                    filteredCount: response.data.data.pagination?.filteredCount || 0,
                    totalPages: response.data.data.pagination?.totalPages || 0,
                    hasNextPage: response.data.data.pagination?.hasNextPage || false,
                    hasPrevPage: response.data.data.pagination?.hasPrevPage || false,
                });
            } else if (response?.data?.status === -1) {
                Message(response.data.message, 'warning');
                setLogContent([]);
            }
        } catch (error) {
            if (!isMounted.current) return;
            console.error('載入日誌失敗:', error);

            // 檢查是否有後端回傳的錯誤訊息
            const errorMessage = error.response?.data?.message;
            const errorStatus = error.response?.data?.status;

            // 只有在非「找不到檔案」的情況下才顯示錯誤訊息
            if (errorStatus === -1 && errorMessage) {
                // 找不到檔案，不顯示訊息，只清空內容
            } else if (errorMessage) {
                Message(errorMessage, 'error');
            } else {
                Message('載入日誌失敗', 'error');
            }
            setLogContent([]);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        getDeliver()
            .then(response => {
                if (response?.status === 200) {
                    setDelivers(response.data.result || []);
                } else {
                    console.log('API狀態非200:', response?.status);
                }
            })
            .catch(err => {
                console.error('派車單API錯誤:', err);
                console.error('錯誤message:', err.message);
                console.error('錯誤response:', err.response);
            });


        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };

    }, []);

    // 初始載入日期列表 - 只執行一次
    useEffect(() => {
        if (role < roleConfig['維護者'] || isInitialized.current) return;
        isInitialized.current = true;

        const loadDates = async () => {
            setLoadingDates(true);
            try {
                const response = await getLogDates();
                if (!isMounted.current) return;
                if (response?.data?.status === 1) {
                    const dates = response.data.data.dates;
                    const typeAvail = response.data.data.typeAvailability;
                    setAvailableDates(dates);
                    setTypeAvailability(typeAvail);
                    if (dates.length > 0) {
                        setSelectedDate(dates[0]);
                        // 直接載入第一個日期的日誌
                        fetchLogs(dates[0], 'all', 1, 100, '', '', '', 'desc');
                    }
                }
            } catch (error) {
                if (!isMounted.current) return;
                console.error('載入日期失敗:', error);
                Message('載入日期列表失敗', 'error');
            } finally {
                if (isMounted.current) {
                    setLoadingDates(false);
                }
            }
        };

        loadDates();
    }, [role]);

    // 權限檢查
    if (role < roleConfig['維護者']) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    權限不足，只有維護者（role = {roleConfig['維護者']}）才能查看日誌
                </div>
            </div>
        );
    }

    // 取得日誌行的 CSS class（只檢查開頭的級別標記）
    const getLogLineClass = (line) => {
        // 只檢查前 35 個字元，避免誤判 response body 內容
        const header = line.substring(0, 35);
        if (header.includes('[ERROR]') || header.includes('[error]')) return 'log-line-error';
        if (header.includes('[WARN]') || header.includes('[warn]') || header.includes('[WARNING]') || header.includes('[warning]')) return 'log-line-warning';
        if (header.includes('[INFO]') || header.includes('[info]')) return 'log-line-info';
        return '';
    };

    // 取得可用的類型選項
    const getAvailableTypes = () => {
        if (!selectedDate || !typeAvailability[selectedDate]) {
            return logTypes;
        }
        return logTypes.filter(type =>
            typeAvailability[selectedDate].includes(type.value)
        );
    };

    // 下載日誌檔案
    const handleDownload = () => {
        const content = logContent.join('\n');
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `log-${selectedDate}-${selectedType}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // 分頁處理
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages && !loading) {
            fetchLogs(selectedDate, selectedType, newPage, pageSize, startTime, endTime, searchKeyword, sortOrder, deliverId);
        }
    };

    // 搜尋處理
    const handleSearch = () => {
        setSearchKeyword(keyword);
        fetchLogs(selectedDate, selectedType, 1, pageSize, startTime, endTime, keyword, sortOrder, deliverId);
    };

    // 清除過濾
    const handleClearFilters = () => {
        setStartTime('');
        setEndTime('');
        setKeyword('');
        setSearchKeyword('');
        fetchLogs(selectedDate, selectedType, 1, pageSize, '', '', '', sortOrder, deliverId);
    };

    // 使用者變更處理
    const handleUserIdChange = (type) => {
        setDeliverId(type);
        fetchLogs(selectedDate, type, 1, pageSize, startTime, endTime, searchKeyword, sortOrder, deliverId);
    };

    // 日期變更處理
    const handleDateChange = (date) => {
        setSelectedDate(date);
        fetchLogs(date, selectedType, 1, pageSize, startTime, endTime, searchKeyword, sortOrder, deliverId);
    };

    // 類型變更處理
    const handleTypeChange = (type) => {
        setSelectedType(type);
        fetchLogs(selectedDate, type, 1, pageSize, startTime, endTime, searchKeyword, sortOrder, deliverId);
    };

    // 每頁筆數變更處理
    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        fetchLogs(selectedDate, selectedType, 1, newSize, startTime, endTime, searchKeyword, sortOrder, deliverId);
    };

    // 重新載入
    const handleRefresh = () => {
        fetchLogs(selectedDate, selectedType, currentPage, pageSize, startTime, endTime, searchKeyword, sortOrder, deliverId);
    };

    // 切換排序
    const handleSortToggle = () => {
        const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
        setSortOrder(newOrder);
        fetchLogs(selectedDate, selectedType, 1, pageSize, startTime, endTime, searchKeyword, newOrder, deliverId);
    };

    // 計算顯示的行號起始值
    const getLineNumberStart = () => {
        return (currentPage - 1) * pageSize + 1;
    };

    return (
        <div className="log-viewer container mt-4">
            <div className="card shadow border-0">
                <div className="card-header bg-primary text-white">
                    <h4 className="mb-0">
                        <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                        系統日誌查看器
                    </h4>
                </div>
                <div className="card-body">
                    {/* 第一列：日期和類型選擇 */}
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label className="form-label fw-bold">選擇日期</label>
                            <select
                                className="form-select"
                                value={selectedDate}
                                onChange={(e) => handleDateChange(e.target.value)}
                                disabled={loadingDates}
                            >
                                {loadingDates && <option value="">載入中...</option>}
                                {!loadingDates && availableDates.length === 0 && <option value="">無可用日期</option>}
                                {!loadingDates && availableDates.map(date => (
                                    <option key={date} value={date}>{date}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">選擇使用者</label>
                            <select
                                className="form-select"
                                value={selectedDate}
                                onChange={(e) => handleUserIdChange(e.target.value)}
                                disabled={loadingDates}
                            >
                                {loadingDates && <option value="">載入中...</option>}
                                {!loadingDates && delivers.length === 0 && <option value="">無使用者</option>}
                                {!loadingDates && delivers.map(deliver => (
                                    <option key={deliver.id} value={deliver.id}>{deliver.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">日誌類型</label>
                            <select
                                className="form-select"
                                value={selectedType}
                                onChange={(e) => handleTypeChange(e.target.value)}
                            >
                                {getAvailableTypes().map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label fw-bold">開始時間</label>
                            <input
                                type="time"
                                step="1"
                                className="form-control"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label fw-bold">結束時間</label>
                            <input
                                type="time"
                                step="1"
                                className="form-control"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button
                                className="btn btn-outline-secondary w-100"
                                onClick={handleClearFilters}
                            >
                                清除過濾
                            </button>
                        </div>
                    </div>

                    {/* 第二列：關鍵字搜尋和下載 */}
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <label className="form-label fw-bold">關鍵字搜尋</label>
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="輸入關鍵字後按搜尋..."
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSearch}
                                >
                                    <FontAwesomeIcon icon={faSearch} className="me-1" />
                                    搜尋
                                </button>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">每頁筆數</label>
                            <select
                                className="form-select"
                                value={pageSize}
                                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                            >
                                <option value="50">50 筆</option>
                                <option value="100">100 筆</option>
                                <option value="200">200 筆</option>
                                <option value="500">500 筆</option>
                            </select>
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                            <button
                                className="btn btn-outline-primary w-100"
                                onClick={handleDownload}
                                disabled={logContent.length === 0}
                            >
                                <FontAwesomeIcon icon={faDownload} className="me-1" />
                                下載當前頁
                            </button>
                        </div>
                    </div>

                    {/* 資訊列 */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="text-muted">
                            {fileName && (
                                <span className="me-3">
                                    <FontAwesomeIcon icon={faFile} className="me-1" />
                                    {fileName}
                                </span>
                            )}
                            <span className="badge bg-secondary me-2">
                                總行數: {pagination.totalLines}
                            </span>
                            {pagination.filteredCount !== pagination.totalLines && (
                                <span className="badge bg-info me-2">
                                    過濾後: {pagination.filteredCount}
                                </span>
                            )}
                            <span className="badge bg-primary">
                                第 {currentPage} / {pagination.totalPages || 1} 頁
                            </span>
                        </div>
                        <div className="d-flex gap-2">
                            <button
                                className={`btn btn-sm ${sortOrder === 'desc' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={handleSortToggle}
                                disabled={loading}
                                title={sortOrder === 'desc' ? '目前：新到舊' : '目前：舊到新'}
                            >
                                <FontAwesomeIcon icon={sortOrder === 'desc' ? faSortAmountDown : faSortAmountUp} />
                                {' '}{sortOrder === 'desc' ? '新→舊' : '舊→新'}
                            </button>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
                                {' '}重新載入
                            </button>
                        </div>
                    </div>

                    {/* 日誌內容 */}
                    <div className="log-content-container">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">載入中...</span>
                                </div>
                                <p className="mt-2 text-muted">載入日誌中...</p>
                            </div>
                        ) : logContent.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <FontAwesomeIcon icon={faInbox} size="3x" className="mb-3" />
                                <p>無符合條件的日誌</p>
                            </div>
                        ) : (
                            <div className="log-content">
                                {logContent.map((line, index) => (
                                    <div
                                        key={`${currentPage}-${index}`}
                                        className={`log-line ${getLogLineClass(line)}`}
                                    >
                                        <span className="log-line-number">
                                            {getLineNumberStart() + index}
                                        </span>
                                        <span className="log-line-content">{line}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* 分頁控制 */}
                    {pagination.totalPages > 1 && (
                        <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handlePageChange(1)}
                                disabled={!pagination.hasPrevPage || loading}
                                title="第一頁"
                            >
                                <FontAwesomeIcon icon={faAngleDoubleLeft} />
                            </button>
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={!pagination.hasPrevPage || loading}
                                title="上一頁"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>

                            <span className="mx-3">
                                第
                                <input
                                    type="number"
                                    className="form-control form-control-sm d-inline-block mx-1"
                                    style={{ width: '60px' }}
                                    value={currentPage}
                                    min={1}
                                    max={pagination.totalPages}
                                    onChange={(e) => {
                                        const page = parseInt(e.target.value);
                                        if (page >= 1 && page <= pagination.totalPages) {
                                            handlePageChange(page);
                                        }
                                    }}
                                />
                                / {pagination.totalPages} 頁
                            </span>

                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={!pagination.hasNextPage || loading}
                                title="下一頁"
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handlePageChange(pagination.totalPages)}
                                disabled={!pagination.hasNextPage || loading}
                                title="最後一頁"
                            >
                                <FontAwesomeIcon icon={faAngleDoubleRight} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export { LogViewer };
